// crewbit-admin/src/components/money/InvoicesTable.tsx
//
// Admin /money/invoices table. 7 columns with sortable headers via shared
// SortableHeader + useSortState. Rows render InvoicesTableRow. Empty +
// loading branches handled by parent (MoneyInvoicesPage).
//
// SPEC: §Money.InvoicesListAdmin §3 (D1).

import { palette } from '../../styles/tokens';
import { SortableHeader } from '../common/SortableHeader';
import { InvoicesTableRow, type InvoiceRowAction } from './InvoicesTableRow';
import type { SortControls } from '../../hooks/useSortState';
import type { InvoiceRow } from '../../types';

interface InvoicesTableProps {
  invoices: InvoiceRow[];
  sort: SortControls;
  onRowClick: (invoiceId: string) => void;
  onActionClick: (invoice: InvoiceRow, action: InvoiceRowAction) => void;
  /** §Money.OverviewBulkOperations: optional selection state. When provided,
   *  the table renders a checkbox column. When undefined, no checkboxes. */
  selectedIds?: Set<string>;
  onToggleSelect?: (invoiceId: string) => void;
  onToggleSelectAll?: () => void;
}

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 11,
  fontWeight: 600,
  color: palette.g400,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`,
  background: palette.g50,
  whiteSpace: 'nowrap',
};

export function InvoicesTable({
  invoices,
  sort,
  onRowClick,
  onActionClick,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: InvoicesTableProps) {
  const selectionEnabled = !!selectedIds && !!onToggleSelect;
  const allSelected = selectionEnabled && invoices.length > 0 && invoices.every((i) => selectedIds!.has(i.id));
  const someSelected = selectionEnabled && invoices.some((i) => selectedIds!.has(i.id));

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {selectionEnabled && (
            <th style={{ ...thStyle, width: 36, textAlign: 'center', cursor: 'default' }}>
              <input
                type="checkbox"
                aria-label={allSelected ? 'Deselect all on page' : 'Select all on page'}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={() => onToggleSelectAll?.()}
                onClick={(e) => e.stopPropagation()}
              />
            </th>
          )}
          <SortableHeader column="invoice_number" label="Invoice #" sort={sort} style={thStyle} />
          <SortableHeader column="client_name" label="Client" sort={sort} style={thStyle} />
          <SortableHeader column="total" label="Total" sort={sort} align="right" style={thStyle} />
          <SortableHeader column="status" label="Status" sort={sort} style={thStyle} />
          <SortableHeader column="created_at" label="Created" sort={sort} style={thStyle} />
          <SortableHeader column="due_date" label="Due" sort={sort} style={thStyle} />
          <th style={{ ...thStyle, textAlign: 'right', cursor: 'default' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((invoice) => (
          <InvoicesTableRow
            key={invoice.id}
            invoice={invoice}
            onRowClick={onRowClick}
            onActionClick={onActionClick}
            selected={selectionEnabled ? selectedIds!.has(invoice.id) : undefined}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </tbody>
    </table>
  );
}
